import { useEffect, useState } from "react";
import {
  ArrowUpRight, ChevronRight, Cpu, Microscope,
  Phone, Sparkles, Stethoscope, Target, TrendingUp,
  MapPin, Mail, Globe, Award, ExternalLink, X as XIcon
} from "lucide-react";
import NeuralBackground from "./components/NeuralBackground";
import MusicToggle from "./components/MusicToggle";
import StockChart from "./components/StockChart";
import TouchSplash from "./components/TouchSplash";
import MelanomaCarousel from "./components/MelanomaCarousel";
import TestingResults from "./components/TestingResults";

const LOGO = "/images/logo-main.webp";
const LOGO_ICON = "/images/logo-icon.webp";

// Original CDN images preserved (from medicalcaretechnologies.com)
const IMG = {
  hero: "https://img1.wsimg.com/isteam/ip/605bcd39-e504-45b9-ba76-8b54a359a79e/MDCE%20Company%20Banner.png/:/cr=t:0%25,l:0%25,w:100%25,h:100%25/rs=h:900,cg:true",
  skinCancer: "https://img1.wsimg.com/isteam/ip/605bcd39-e504-45b9-ba76-8b54a359a79e/MDCE%20Skin%20Cancer%20Screening%20Collage.png/:/rs=w:900,h:900,cg:true,m/cr=w:900,h:900",
  wellness: "https://img1.wsimg.com/isteam/ip/605bcd39-e504-45b9-ba76-8b54a359a79e/MDCE%20AI%20Lifestyle%20and%20Wellness%20Collage.png/:/rs=w:900,h:900,cg:true,m/cr=w:900,h:900",
  wound: "https://img1.wsimg.com/isteam/ip/605bcd39-e504-45b9-ba76-8b54a359a79e/MDCE%20AI%20Wound%20Monitoring.png/:/rs=w:900,h:900,cg:true,m/cr=w:900,h:900",
  snapshotBanner: "https://img1.wsimg.com/isteam/ip/605bcd39-e504-45b9-ba76-8b54a359a79e/SnapShot%20Recipes%20Webpage%20Banner.png/:/cr=t:0%25,l:0%25,w:100%25,h:100%25/rs=w:900,h:900,cg:true",
  melanoma1: "https://img1.wsimg.com/isteam/ip/605bcd39-e504-45b9-ba76-8b54a359a79e/Melanoma%20Scan%20Collage%201.png/:/cr=t:0%25,l:0%25,w:100%25,h:100%25",
  melanoma2: "https://img1.wsimg.com/isteam/ip/605bcd39-e504-45b9-ba76-8b54a359a79e/Melanoma%20Scan%202.png/:/",
  ceo: "https://img1.wsimg.com/isteam/ip/605bcd39-e504-45b9-ba76-8b54a359a79e/Miracle%20CEO%20Marshall%20Perkins.png/:/cr=t:0%25,l:0%25,w:100%25,h:100%25/rs=w:900,cg:true",
  snapshotNew: "https://img1.wsimg.com/isteam/ip/605bcd39-e504-45b9-ba76-8b54a359a79e/Snapshot%20Recipes%20New%20Look%20Banner.png/:/cr=t:0%25,l:0%25,w:100%25,h:100%25",
  consign: "https://img1.wsimg.com/isteam/ip/605bcd39-e504-45b9-ba76-8b54a359a79e/Instant%20%24250%20for%20Every%20%2410%2C000%20Consigned-9.png/:/cr=t:0%25,l:0%25,w:100%25,h:100%25",
  infinite: "https://img1.wsimg.com/isteam/ip/605bcd39-e504-45b9-ba76-8b54a359a79e/Black%20and%20Red%20Modern%20New%20Collection%20Sneakers%20P.png",
  rgu: "https://img1.wsimg.com/isteam/ip/605bcd39-e504-45b9-ba76-8b54a359a79e/Copy%20of%20Copy%20of%20Copy%20of%20Black%20and%20Teal%20Modern%20.png",
};

const NAV = [
  { id: "applications", label: "Applications" },
  { id: "testing-results", label: "Testing" },
  { id: "about", label: "About" },
  { id: "roadmap", label: "Roadmap" },
  { id: "leadership", label: "Leadership" },
  { id: "subsidiaries", label: "Subsidiaries" },
  { id: "stock", label: "Stock" },
  { id: "contact", label: "Contact" },
];

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2" : "py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8">
        <div
          className={`flex items-center justify-between rounded-full pl-3 pr-3 sm:pl-4 sm:pr-4 lg:px-6 py-2 sm:py-2.5 gap-3 transition-all duration-500 ${
            scrolled
              ? "bg-[rgba(11,6,18,0.78)] backdrop-blur-xl border border-[rgba(160,107,255,0.22)] shadow-[0_20px_50px_-30px_rgba(138,61,255,0.6)]"
              : "bg-transparent border border-transparent"
          }`}
        >
          <a href="#top" data-testid="nav-logo" className="flex items-center gap-2.5 sm:gap-3 group min-w-0 lg:hidden">
            <img
              src={LOGO_ICON}
              alt="Medical Care Technologies Inc."
              className="h-9 w-9 sm:h-11 sm:w-11 object-contain shrink-0"
            />
            <div className="flex flex-col leading-tight min-w-0">
              <span className="font-display text-[12px] sm:text-[15px] font-semibold tracking-wide text-white whitespace-nowrap">
                MEDICAL CARE
              </span>
              <span className="font-mono-tech text-[7.5px] sm:text-[9px] text-[#d6b8ff] whitespace-nowrap">
                TECHNOLOGIES INC.<span className="hidden sm:inline"> (OTC TICKER: MDCE)</span>
              </span>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                data-testid={`nav-link-${n.id}`}
                className="px-4 py-2 text-sm text-[#cfc4e6] hover:text-white transition-colors rounded-full hover:bg-[rgba(160,107,255,0.08)]"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href="#contact"
              data-testid="nav-cta-button"
              className="!hidden lg:!inline-flex btn-primary !py-2.5 !px-5 text-sm whitespace-nowrap"
            >
              Investor Contact <ArrowUpRight size={16} />
            </a>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-[rgba(160,107,255,0.3)] text-white"
              aria-label="Toggle menu"
            >
              <span className="block w-4 h-px bg-white relative before:content-[''] before:absolute before:-top-1.5 before:left-0 before:w-4 before:h-px before:bg-white after:content-[''] after:absolute after:top-1.5 after:left-0 after:w-4 after:h-px after:bg-white" />
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden mt-2 glass-panel p-4 flex flex-col gap-1">
            <a
              href="#top"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-link-home"
              className="px-4 py-3 text-sm text-[#cfc4e6] hover:text-white hover:bg-[rgba(160,107,255,0.08)] rounded-xl"
            >
              Home
            </a>
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${n.id}`}
                className="px-4 py-3 text-sm text-[#cfc4e6] hover:text-white hover:bg-[rgba(160,107,255,0.08)] rounded-xl"
              >
                {n.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative pt-24 sm:pt-32 lg:pt-44 pb-24 lg:pb-32">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center lg:items-start">
          <div className="lg:col-span-7 fade-in-up">
            {/* Desktop-only full branded logo, sitting just above the hero pill */}
            <div className="hidden lg:flex items-center gap-3 mb-5" data-testid="hero-brand-block">
              <img
                src={LOGO_ICON}
                alt=""
                className="h-10 w-10 object-contain"
              />
              <div className="flex flex-col leading-tight">
                <span className="font-display text-[16px] font-semibold tracking-wide text-white">
                  MEDICAL CARE TECHNOLOGIES INC.
                </span>
                <span className="font-mono-tech text-[10px] text-[#d6b8ff] tracking-[0.2em]">
                  OTC TICKER: MDCE
                </span>
              </div>
            </div>
            <span data-testid="hero-tag" className="tag-pill">
              <span className="dot" />
              OTC PINK · MDCE · AI HEALTH PLATFORM
            </span>
            <h1
              data-testid="hero-headline"
              className="font-display mt-6 text-[44px] sm:text-[56px] lg:text-[64px] xl:text-[72px] 2xl:text-[78px] leading-[1.02] lg:leading-[0.98] font-bold tracking-tight"
            >
              <span className="text-gradient-violet lg:whitespace-nowrap">AI vision technology</span>
              <br />
              <span className="text-white">built for people —</span>
              <br />
              <span className="shimmer-text lg:whitespace-nowrap">and the industries</span>
              <br />
              <span className="text-white lg:whitespace-nowrap">around them.</span>
            </h1>
            <p
              data-testid="hero-subheadline"
              className="mt-7 text-lg text-[#cfc4e6] max-w-2xl leading-relaxed"
            >
              A diversified company at the intersection of health, wellness, and medical AI.
              Our proprietary imaging engine transforms ordinary smartphones into
              real-time diagnostic tools — built on deep learning, temporal image
              analysis, and affective computing.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a href="#applications" data-testid="hero-cta-explore" className="btn-primary">
                Explore Applications <ChevronRight size={18} />
              </a>
              <a href="#about" data-testid="hero-cta-about" className="btn-ghost">
                <Sparkles size={16} /> About MDCE
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-5 max-w-xl">
              {[
                { k: "127", l: "Target Countries" },
                { k: "30", l: "Languages" },
                { k: "2026", l: "Launch Window" },
              ].map((s) => (
                <div
                  key={s.l}
                  data-testid={`hero-stat-${s.l.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border-l border-[rgba(160,107,255,0.4)] pl-4"
                >
                  <div className="font-display text-3xl font-bold text-white">{s.k}</div>
                  <div className="font-mono-tech text-[10px] text-[#a991d6] mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 fade-in-up lg:mt-12 xl:mt-14" style={{ animationDelay: "180ms" }}>
            <div className="relative">
              <div className="absolute -inset-6 bg-[radial-gradient(circle_at_50%_50%,rgba(138,61,255,0.4),transparent_70%)] blur-2xl" />
              <div className="relative image-frame corner-accents scan-line">
                <img
                  src={LOGO}
                  alt="Medical Care Technologies Logo"
                  data-testid="hero-logo-image"
                  className="w-full aspect-square object-contain bg-[#050208] p-8"
                />
              </div>

              <div className="absolute -top-5 -right-5 glass-panel px-4 py-3 hidden sm:flex items-center gap-3">
                <div className="h-9 w-9 grid place-items-center rounded-full bg-[rgba(160,107,255,0.18)] border border-[rgba(160,107,255,0.45)]">
                  <Cpu size={16} className="text-[#d6b8ff]" />
                </div>
                <div>
                  <div className="text-[11px] font-mono-tech text-[#a991d6]">PROPRIETARY</div>
                  <div className="text-sm text-white">Deep Learning Engine</div>
                </div>
              </div>
            </div>

            {/* Mobile-only proprietary badge (desktop unchanged) */}
            <div className="mt-5 flex sm:hidden" data-testid="mobile-hero-badges">
              <div className="glass-panel px-3 py-2.5 flex items-center gap-2 w-full">
                <div className="h-8 w-8 grid place-items-center rounded-full bg-[rgba(160,107,255,0.18)] border border-[rgba(160,107,255,0.45)] shrink-0">
                  <Cpu size={14} className="text-[#d6b8ff]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[9px] font-mono-tech text-[#a991d6] tracking-[0.18em]">PROPRIETARY</div>
                  <div className="text-[11px] text-white leading-tight">Deep Learning Engine</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20 divider-glow" />
      </div>
    </section>
  );
}

function ApplicationCard({ num, tag, title, body, image, reverse }) {
  return (
    <div
      data-testid={`application-card-${num}`}
      className={`grid lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
        reverse ? "lg:[direction:rtl]" : ""
      }`}
    >
      <div className={`lg:col-span-5 ${reverse ? "lg:[direction:ltr]" : ""}`}>
        <div className="relative">
          <div className="absolute -inset-4 bg-[radial-gradient(circle_at_50%_50%,rgba(138,61,255,0.35),transparent_70%)] blur-2xl" />
          <div className="relative image-frame corner-accents bg-[#0b0612]">
            <img
              src={image}
              alt={title}
              className="w-full aspect-square object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </div>
      <div className={`lg:col-span-7 ${reverse ? "lg:[direction:ltr]" : ""}`}>
        <div className="section-num">APPLICATION · 0{num}</div>
        <div className="mt-2 font-mono-tech text-[11px] text-[#d6b8ff]">{tag}</div>
        <h3 className="font-display text-3xl lg:text-5xl font-bold text-white mt-4 leading-[1.05]">
          {title}
        </h3>
        <p className="mt-5 text-[#cfc4e6] leading-relaxed text-[17px]">{body}</p>
      </div>
    </div>
  );
}

function Applications() {
  return (
    <section id="applications" className="relative pt-4 pb-20 lg:pt-8 lg:pb-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-24 lg:space-y-32">
        <div className="text-center max-w-3xl mx-auto">
          <div className="section-num">02 / APPLICATIONS</div>
          <h2 className="font-display text-4xl lg:text-6xl font-bold mt-3 leading-[1.05]">
            <span className="text-white">A scalable, AI-powered </span>
            <span className="text-gradient-violet">health ecosystem.</span>
          </h2>
          <p className="mt-5 text-[#cfc4e6] text-lg">
            Multiple verticals. One proprietary engine. Designed to redefine digital
            diagnostics and preventative healthcare worldwide.
          </p>
        </div>

        {/* Melanoma Scan Beta — futuristic mobile carousel */}
        <div className="mt-4 lg:mt-2" data-testid="melanoma-scan-block">
          <div className="text-center max-w-3xl mx-auto mb-8 lg:mb-10">
            <div className="section-num">FLAGSHIP BETA · LIVE</div>
            <h3 className="font-display text-3xl lg:text-5xl font-bold mt-3 leading-[1.05]">
              <span className="text-white">MDCE </span>
              <span className="text-gradient-violet">Melanoma Scan</span>
              <span className="text-white"> Beta</span>
            </h3>
            <p className="mt-4 text-[#a991d6] font-mono-tech text-[11px] tracking-[0.24em]">
              AI-POWERED SKIN CANCER PRE-SCREEN · MOBILE PREVIEW
            </p>
          </div>
          <MelanomaCarousel />
        </div>

        {/* StrainScan Pro — enterprise iPad Air deployment, cross-vertical AI */}
        <StrainScanShowcase />

        <ApplicationCard
          num={1}
          tag="DERMATOLOGICAL SURVEILLANCE"
          title="AI Skin Cancer Pre-Screening"
          body="Imagine a transformative AI solution that elevates dermatological surveillance by turning routine skin photos into a dynamic, longitudinal body map — intelligently tracking every mole, spot, and lesion across the entire skin surface over time. Unlike traditional tools that analyze individual lesions in isolation, this innovation introduces comprehensive, full-body skin monitoring powered by machine learning, capable of detecting subtle shifts in size, shape, and pigmentation across a patient's entire dermatological profile. By creating a visual lineage of skin changes, it dramatically improves early detection of melanoma, basal cell carcinoma, and squamous cell carcinoma, while enhancing triage prioritization and long-term screening accuracy."
          image={IMG.skinCancer}
        />

        <ApplicationCard
          num={2}
          reverse
          tag="LIFESTYLE & WELLNESS"
          title="AI Lifestyle & Wellness Applications"
          body="At Medical Care Technologies Inc., we are thrilled to unveil our new suite of AI Lifestyle and Wellness Applications. Leveraging our proprietary AI imaging analysis technology, each app in this category is designed with an artificial intelligence edge that sets it apart from traditional wellness tools. This new category encompasses applications focused on dietary insights, nutritional guidance, fitness tracking, and overall general health. By integrating AI-driven personalization, these apps deliver tailored wellness experiences that appeal to a wide range of users — not just those with specific medical conditions."
          image={IMG.wellness}
        />

        <ApplicationCard
          num={3}
          tag="REMOTE PATIENT MONITORING"
          title="AI Wound-Healing Monitor for Home Care"
          body="Picture an AI-powered health solution that transforms everyday smartphone cameras into intelligent wound monitoring tools — capturing sequential images of chronic wounds like diabetic ulcers to track healing trajectories, detect infection risks, and identify stagnation or deterioration in real time. This system leverages automated segmentation, edge detection, and visual pattern recognition to quantify progress with clinical-level precision, all without requiring in-person evaluation. Its true innovation lies in empowering non-clinical users — patients, caregivers, and remote practitioners — with data-driven insights that enable earlier interventions, reduce hospital visits, and improve outcomes."
          image={IMG.wound}
        />

      </div>
    </section>
  );
}

function EnterpriseVision() {
  const promo1 = "/images/banner-1.webp";
  const promo2 = "/images/banner-2.webp";

  return (
    <section
      id="enterprise-vision"
      data-testid="enterprise-vision-section"
      className="relative py-20 lg:py-28"
    >
      {/* Soft ambient glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[60rem] h-[40rem] bg-[radial-gradient(ellipse_at_center,rgba(138,61,255,0.18),transparent_70%)] blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <div className="section-num">04 / ENTERPRISE</div>
          <h2 className="font-display text-4xl lg:text-6xl font-bold mt-3 leading-[1.05]">
            <span className="text-white">Enterprise Level </span>
            <span className="text-gradient-violet">Vision Analysis Solutions</span>
          </h2>
          <p className="mt-5 text-[#cfc4e6] text-lg leading-relaxed">
            Transforming images. Enabling intelligent decisions. Our AI-driven
            vision analysis technology delivers real-time insights, automation,
            and accuracy across a wide range of enterprise applications.
          </p>
        </div>

        {/* Promo cards */}
        <div className="mt-12 lg:mt-16 grid md:grid-cols-2 gap-6 lg:gap-8 items-start">
          {[
            {
              src: promo1,
              alt: "AI-Powered Vision Analysis Solutions — Transforming Images. Enabling Intelligent Decisions.",
              tag: "OFFERING · 01",
              title: "AI-Powered Vision Analysis",
              caption: "Empowering industries with smarter vision solutions.",
            },
            {
              src: promo2,
              alt: "Vision AI API Solutions — Power Your Platform. Elevate Your Capabilities.",
              tag: "OFFERING · 02",
              title: "Vision AI API",
              caption: "Built for innovation. Designed for impact.",
            },
          ].map((p, i) => (
            <div
              key={p.title}
              data-testid={`enterprise-promo-${i + 1}`}
              className="group fade-in-up"
              style={{ animationDelay: `${i * 140}ms` }}
            >
              <div className="relative image-frame corner-accents bg-[#0b0612] overflow-hidden">
                <div className="absolute -inset-8 bg-[radial-gradient(circle_at_50%_50%,rgba(138,61,255,0.28),transparent_70%)] blur-2xl pointer-events-none" />
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className="relative w-full h-auto object-contain block transition-transform duration-700 ease-out group-hover:scale-[1.015]"
                />
                {/* Top-right HUD stamp */}
                <div className="absolute top-4 right-4 z-10 hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(11,6,18,0.72)] border border-[rgba(160,107,255,0.45)] backdrop-blur">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#a06bff] animate-pulse" />
                  <span className="font-mono-tech text-[10px] tracking-[0.22em] text-[#d6b8ff]">
                    {p.tag}
                  </span>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <div className="font-display text-white text-lg lg:text-xl">
                  {p.title}
                </div>
                <div className="font-mono-tech text-[10px] tracking-[0.22em] text-[#a991d6] hidden md:block">
                  {p.caption.toUpperCase()}
                </div>
              </div>
              <p className="mt-1 text-[#b9add0] text-sm md:hidden leading-relaxed">
                {p.caption}
              </p>
            </div>
          ))}
        </div>

        {/* Capability strip */}
        <div className="mt-12 lg:mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          {[
            { k: "Advanced AI", v: "Deep-learning vision models" },
            { k: "Secure & Compliant", v: "Privacy-first architecture" },
            { k: "Scalable Platform", v: "Enterprise-grade throughput" },
            { k: "Seamless Integration", v: "Well-documented API" },
          ].map((c, i) => (
            <div
              key={c.k}
              data-testid={`enterprise-capability-${i + 1}`}
              className="glass-panel p-4 lg:p-5"
            >
              <div className="font-mono-tech text-[10px] tracking-[0.22em] text-[#a06bff]">
                CAPABILITY · 0{i + 1}
              </div>
              <div className="mt-2 font-display text-white text-base lg:text-[17px]">
                {c.k}
              </div>
              <div className="mt-1 text-[12px] lg:text-[13px] text-[#b9add0] leading-relaxed">
                {c.v}
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center font-mono-tech text-[11px] tracking-[0.24em] text-[#a991d6]">
          ENTERPRISE SOLUTIONS · REAL-WORLD IMPACT · PARTNER WITH US
        </p>

        <div className="mt-6 flex justify-center">
          <a
            href="mailto:marshall@medicalcaretechnologies.com?subject=Enterprise%20Vision%20Analysis%20Inquiry&body=Hello%20Medical%20Care%20Technologies%20team%2C%0A%0AI%27m%20interested%20in%20learning%20more%20about%20your%20Enterprise%20Vision%20Analysis%20Solutions.%0A%0A"
            data-testid="enterprise-partner-cta"
            className="btn-primary group"
          >
            Partner With Us
            <ArrowUpRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="relative py-20 lg:py-28">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="section-num">05 / ABOUT</div>
          <h2 className="font-display text-4xl lg:text-6xl font-bold text-white mt-3 leading-[1.05]">
            About <span className="text-gradient-violet">Medical Care Technologies Inc.</span>
          </h2>
        </div>
        <div className="space-y-5 text-[#cfc4e6] leading-relaxed text-[17px] lg:text-[18px]">
          <p>
            <span className="text-white font-semibold">Medical Care Technologies Inc. (OTC Pink: MDCE)</span>{" "}
            is a diversified technology company focused on the development and
            commercialization of <span className="text-white">artificial intelligence-powered
            applications</span> spanning health, wellness, nutrition, lifestyle, and
            enterprise imaging analysis solutions.
          </p>
          <p>
            The Company is actively expanding its artificial intelligence initiatives
            through a growing portfolio of consumer-facing and healthcare-oriented
            applications, including projects in <span className="text-white">dietary
            intelligence</span>, <span className="text-white">preventive health imaging</span>,
            and <span className="text-white">enterprise-level vision analysis</span>.
            Medical Care Technologies' strategic focus is centered on identifying
            commercially viable AI opportunities that can scale across multiple sectors
            as artificial intelligence capabilities continue to rapidly evolve.
          </p>
          <p>
            Through its consumer AI initiatives, including the{" "}
            <span className="text-white">Snapshot Recipes</span> mobile platform, and
            emerging healthcare-focused beta projects such as{" "}
            <span className="text-white">MDCE Melanoma Scan</span>, the Company is
            positioning itself to participate in high-growth segments of the digital
            health and AI application economy.
          </p>
          <p>
            Medical Care Technologies also owns subsidiaries{" "}
            <span className="text-white">Infinite Auctions</span> and{" "}
            <span className="text-white">Real Game Used</span>, which provide
            complementary revenue-generating business operations in the collectibles,
            authentication, and image analysis sectors.
          </p>
          <p>
            As AI technology continues advancing at an accelerated pace, the Company
            remains focused on adapting its innovation strategy to leverage the most
            effective technologies available, with the objective of building
            <span className="text-white"> scalable AI-powered products and commercial
            solutions</span> for both consumers and enterprise clients.
          </p>
          <p>
            As part of this ongoing evolution, Medical Care Technologies has moved
            beyond its earlier patent-pending AI imaging framework, as the underlying
            artificial intelligence capabilities have advanced substantially ahead of
            the scope originally contemplated by that filing. The Company remains
            focused on adopting current and forward-looking AI approaches best aligned
            with today's rapidly evolving development landscape.
          </p>
          <p>
            The Company intends to pursue the launch and expansion of multiple
            artificial intelligence-integrated applications across global markets,
            subject to development progress, market conditions, regulatory
            considerations, and available capital resources.
          </p>
        </div>
      </div>
    </section>
  );
}

function Roadmap() {
  const phases = [
    {
      tag: "SHORT-TERM EXECUTION",
      title: "Coming Weeks / Months",
      icon: Target,
      items: [
        {
          status: "completed",
          heading: "Capital Deployment & Asset Monetization",
          body: "Successfully liquidated approximately $250,000 in subsidiary-owned assets to help fund the Company's artificial intelligence research, development, infrastructure, and production initiatives.",
        },
        {
          status: "completed",
          heading: "Consumer AI Product Launch",
          body: "Successfully launched the significantly enhanced version of the Company's flagship AI-powered mobile application, Snapshot Recipes, featuring major technology improvements, expanded functionality, and a redesigned market positioning strategy intended to improve user acquisition and monetization performance.",
        },
        {
          status: "completed",
          heading: "AI Pipeline Expansion Strategy",
          body: "Publicly outlined the Company's broader strategic initiative to develop multiple artificial intelligence-powered applications across the health, wellness, nutrition, dietary, and lifestyle sectors, with the objective of establishing multiple near-term commercial revenue opportunities.",
        },
        {
          status: "completed",
          heading: "MDCE Melanoma Scan Beta Preview",
          body: "Announced the beta preview of MDCE Melanoma Scan, the Company's healthcare-focused artificial intelligence initiative designed to explore AI-powered image analysis applications in the preventive health and skin wellness monitoring space, representing an early milestone in Medical Care Technologies' broader medical AI expansion strategy.",
        },
        {
          status: "completed",
          heading: "Enterprise AI Commercialization",
          body: "Established the Company's intention to offer custom enterprise-level AI imaging and computer vision analysis solutions for commercial clients seeking specialized AI applications.",
        },
        {
          status: "completed",
          heading: "StrainScan Pro Launch — Enterprise AI Vision",
          body: "Launched StrainScan Pro, a proprietary AI-powered visual-intelligence platform deployable on iPad Air for enterprise clients across multiple verticals including agriculture, foodservice, food logistics, cannabis retail, and end-to-end supply chain quality control — marking the Company's first commercially available enterprise AI product.",
        },
        {
          status: "ongoing",
          heading: "Ongoing Product Development",
          body: "Continue internal development, refinement, and prototype testing of multiple AI-powered mobile and enterprise applications currently in various stages of design and beta development.",
        },
        {
          status: "ongoing",
          heading: "Revenue Generation Through Subsidiaries",
          body: "Continue leveraging subsidiary operations, including Infinite Auctions and Real Game Used, to generate revenue through high-value collectible transactions, memorabilia authentication services, and AI-powered image analysis technologies.",
        },
        {
          status: "ongoing",
          heading: "Growth Capital Initiatives",
          body: "Pursue strategic financing opportunities designed to accelerate the Company's roadmap toward launching a broader ecosystem of advanced AI-powered mobile and enterprise applications.",
        },
      ],
    },
    {
      tag: "NEAR-TERM GROWTH INITIATIVES",
      title: "2026 – 2027",
      icon: TrendingUp,
      items: [
        {
          status: "ongoing",
          heading: "Additional Capital Through Asset Monetization",
          body: "Evaluate the strategic liquidation of an additional approximately $250,000 in subsidiary-owned assets, where appropriate, to further support the advancement of internal AI development initiatives and beta-stage applications.",
        },
        {
          status: "ongoing",
          heading: "Structured Beta Testing Programs",
          body: "Conduct phased internal beta testing across select AI applications, followed by expanded volunteer-based testing programs designed to improve functionality, user experience, and application reliability.",
        },
        {
          status: "planned",
          heading: "Medical-Oriented Testing Collaboration",
          body: "Explore structured beta testing collaborations with medical student groups, research participants, and academic communities, where appropriate, to support early-stage evaluation of medical-oriented AI applications.",
        },
        {
          status: "planned",
          heading: "Medical Beta Application Advancement",
          body: "Advance one or more healthcare-focused AI applications toward fully tested beta-stage readiness, subject to development progress, validation milestones, and resource availability.",
        },
        {
          status: "planned",
          heading: "Clinical Evaluation Pathways",
          body: "Where commercially and scientifically appropriate, evaluate advancement of select applications into formal efficacy testing, clinical validation initiatives, and institutional review processes.",
        },
        {
          status: "planned",
          heading: "AI Platform Expansion",
          body: "Continue expanding the Company's core artificial intelligence imaging and analysis platform to support both consumer-facing applications and medical-oriented diagnostic or risk-assessment use cases.",
        },
      ],
    },
    {
      tag: "LONG-TERM STRATEGIC OBJECTIVES",
      title: "2027 & Beyond",
      icon: Award,
      items: [
        {
          status: "planned",
          heading: "Potential OTCQB Uplisting",
          body: "Evaluate pursuing an uplisting to the OTCQB Venture Market, subject to the Company satisfying all applicable qualification, financial, governance, and compliance requirements, with the objective of enhancing visibility, transparency, and institutional investor accessibility.",
        },
        {
          status: "planned",
          heading: "Intellectual Property Advancement",
          body: "Evaluate the continuation, refinement, expansion, or strategic evolution of the Company's AI image analysis intellectual property portfolio, including provisional and successor filings where appropriate.",
        },
        {
          status: "planned",
          heading: "Clinical & Institutional Partnerships",
          body: "Explore partnerships with healthcare institutions, research organizations, universities, and strategic collaborators to support the advancement and commercialization of medical-focused AI technologies.",
        },
        {
          status: "planned",
          heading: "Regulatory Pathway Exploration",
          body: "For applications intended for regulated medical use, evaluate potential regulatory strategies, including validation studies, FDA-related pathways, and other applicable regulatory frameworks, where appropriate.",
        },
        {
          status: "planned",
          heading: "Subsidiary Strategic Value Creation",
          body: "As Medical Care Technologies achieves sustained operational growth and meaningful revenue expansion, evaluate strategic alternatives for subsidiaries such as Infinite Auctions and Real Game Used, including independent growth strategies, spinout opportunities, or separate public market pathways, subject to market conditions and regulatory considerations.",
        },
      ],
    },
  ];

  const statusMeta = {
    completed: {
      label: "COMPLETED",
      cls: "border-[rgba(74,222,128,0.55)] bg-[rgba(74,222,128,0.10)] text-[#86efac]",
      dot: "bg-[#4ade80] shadow-[0_0_10px_rgba(74,222,128,0.85)]",
    },
    ongoing: {
      label: "ONGOING",
      cls: "border-[rgba(160,107,255,0.55)] bg-[rgba(160,107,255,0.12)] text-[#d6b8ff]",
      dot: "bg-[#a06bff] shadow-[0_0_10px_rgba(160,107,255,0.85)] animate-pulse",
    },
    planned: {
      label: "PLANNED",
      cls: "border-[rgba(160,107,255,0.35)] bg-[rgba(11,6,18,0.6)] text-[#a991d6]",
      dot: "bg-[#a991d6]",
    },
  };

  return (
    <section id="roadmap" className="relative py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="section-num">07 / ROADMAP</div>
            <h2 className="font-display text-4xl lg:text-6xl font-bold mt-3 leading-[1.05]">
              <span className="text-white">MDCE Company </span>
              <span className="text-gradient-violet">Roadmap</span>
            </h2>
          </div>
          <div className="font-mono-tech text-[11px] text-[#a991d6]">
            UPDATED · 05 / 21 / 2026
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {phases.map((p, i) => (
            <div
              key={p.tag}
              data-testid={`roadmap-phase-${i + 1}`}
              className="glass-panel p-7 relative"
            >
              <div className="absolute top-0 left-7 -translate-y-1/2 px-3 py-1 rounded-full bg-[#0b0612] border border-[rgba(160,107,255,0.45)] font-mono-tech text-[10px] text-[#d6b8ff] whitespace-nowrap">
                PHASE 0{i + 1}
              </div>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 grid place-items-center rounded-xl bg-[rgba(160,107,255,0.12)] border border-[rgba(160,107,255,0.35)] shrink-0">
                  <p.icon size={18} className="text-[#d6b8ff]" />
                </div>
                <div className="min-w-0">
                  <div className="font-mono-tech text-[10px] text-[#a06bff] tracking-[0.18em] leading-tight">
                    {p.tag}
                  </div>
                  <div className="font-display text-xl text-white leading-tight mt-0.5">
                    {p.title}
                  </div>
                </div>
              </div>
              <ul className="mt-6 space-y-5">
                {p.items.map((it, j) => {
                  const s = statusMeta[it.status] || statusMeta.planned;
                  return (
                    <li
                      key={j}
                      data-testid={`roadmap-phase-${i + 1}-item-${j + 1}`}
                      className="relative pl-5 border-l border-[rgba(160,107,255,0.22)]"
                    >
                      <span
                        aria-hidden="true"
                        className={`absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full ${s.dot}`}
                      />
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border font-mono-tech text-[9.5px] tracking-[0.18em] ${s.cls}`}
                        >
                          {s.label}
                        </span>
                        <div className="font-display text-white text-[14px] lg:text-[15px] leading-snug">
                          {it.heading}
                        </div>
                      </div>
                      <p className="text-[13px] text-[#b9add0] leading-relaxed">
                        {it.body}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <p className="mt-10 max-w-3xl text-[#b9add0] text-[15px] leading-relaxed">
          The Company believes that its consumer-focused AI applications can help support
          operations and growth while it advances through the longer development, testing,
          and approval timelines required for medical-grade technologies.
        </p>
      </div>
    </section>
  );
}

function Leadership() {
  return (
    <section id="leadership" className="relative py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 bg-[radial-gradient(circle_at_30%_30%,rgba(138,61,255,0.4),transparent_70%)] blur-2xl" />
              <div className="relative image-frame corner-accents bg-[#0b0612]">
                <img
                  src={IMG.ceo}
                  alt="Marshall H. Perkins III - CEO"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-4 left-6 glass-panel px-4 py-2.5">
                <div className="font-mono-tech text-[10px] text-[#a991d6]">CEO</div>
                <div className="text-white font-display text-sm">Marshall H. Perkins III</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="section-num">09 / LEADERSHIP</div>
            <h2 className="font-display text-4xl lg:text-6xl font-bold mt-3 leading-[1.05]">
              <span className="text-white">A walking miracle, </span>
              <span className="text-gradient-violet">with re-ignited purpose.</span>
            </h2>
            <div className="mt-7 space-y-5 text-[#cfc4e6] leading-relaxed text-[17px]">
              <p>
                <span className="text-white font-semibold">Marshall Perkins</span> is the CEO
                of <span className="text-white">Medical Care Technologies Inc., Infinite
                Auctions, and Real Game Used</span>, where he leads with vision, resilience,
                and purpose.
              </p>
              <p>
                In early 2025, Marshall endured a near-fatal battle with advanced bone
                cancer that destroyed multiple vertebrae and left him hospitalized for
                months. At the same time, he faced the devastation of vitiligo. Against
                all odds, he made a full and miraculous recovery — learning to walk again,
                beating cancer, and even reversing the skin condition once thought permanent.
              </p>
              <p>
                Today, Marshall channels this extraordinary second chance into innovation
                and impact. He works hand-in-hand with a dedicated team of
                <span className="text-white"> medical experts, medical students, and tech
                innovators</span>, collaborating daily to push the boundaries of what's
                possible in healthcare and technology.
              </p>
              <p>
                Having nearly lost his life to an unexpected health crisis, Marshall knows
                firsthand how quickly everything can change. He doesn't want others to be
                blindsided the way he was — and that experience has made him more motivated
                than ever to ensure others have the tools, knowledge, and support to
                protect their health before it's too late.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function StrainScanShowcase() {
  const verticals = [
    {
      k: "Agriculture",
      tam: "$5T+",
      body: "Field-side crop disease detection, ripeness scoring, and yield anomaly flagging. Growers walk rows with an iPad Air and log grade-A vs. rejected produce in seconds — no soil labs, no sample shipping, no delays before harvest decisions.",
    },
    {
      k: "Foodservice",
      tam: "$4T+",
      body: "Standardize plating, portion size and ingredient quality across every location. Managers of restaurant chains and commercial kitchens verify prep-line output in real time, cutting recipe-drift complaints and food-cost variance across multi-unit operators.",
    },
    {
      k: "Food Logistics",
      tam: "$300B+",
      body: "Intake grading, cold-chain spot checks and pre-load QC at distribution centers and 3PL hubs. Flag spoiled or under-ripe shipments before they hit shelves — every hand-off captured with a timestamped visual audit trail.",
    },
    {
      k: "Cannabis Retail",
      tam: "Multi-Billion",
      body: "Instant strain identification, THC/CBD/terpene estimation and mold/mildew/pest screening for dispensary networks. Multi-store operators keep every location aligned on quality standards and generate compliance-ready reports on demand.",
    },
    {
      k: "Supply Chain",
      tam: "Farm → Shelf",
      body: "Trace batches from source through every distribution node to point of sale with automated visual audits. One unified vision layer replaces fragmented QC checklists and disconnected inspection tools across the entire operation.",
    },
  ];

  return (
    <div
      className="relative"
      data-testid="strainscan-showcase"
      id="strainscan"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[70rem] h-[42rem] bg-[radial-gradient(ellipse_at_center,rgba(138,61,255,0.20),transparent_70%)] blur-3xl" />
      </div>

      {/* Header */}
      <div className="relative text-center max-w-3xl mx-auto mb-10 lg:mb-12">
        <div className="section-num">FLAGSHIP ENTERPRISE · LIVE</div>
        <h2 className="font-display text-3xl lg:text-5xl font-bold mt-3 leading-[1.05]">
          <span className="text-white">StrainScan </span>
          <span className="text-gradient-violet">Pro</span>
          <span className="text-white"> — Enterprise AI Vision</span>
        </h2>
        <p className="mt-4 text-[#a991d6] font-mono-tech text-[11px] tracking-[0.24em]">
          A PRODUCT OF MEDICAL CARE TECHNOLOGIES INC. · iPAD AIR DEPLOYMENT
        </p>
        <p className="mt-5 text-[#cfc4e6] text-[16px] lg:text-[17px] leading-relaxed">
          A single proprietary visual-intelligence layer for quality control from
          <span className="text-white"> farm to restaurant to dispensary</span> — deployable
          across an entire operation on iPad Air, without heavy infrastructure.
        </p>
      </div>

      {/* iPad Air feature block */}
      <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        {/* LEFT: StrainScan Pro brand image */}
        <div className="lg:col-span-6">
          <div className="relative mx-auto max-w-[540px]">
            {/* Aura */}
            <div className="absolute -inset-6 bg-[radial-gradient(circle_at_50%_40%,rgba(74,222,128,0.28),rgba(160,107,255,0.20)_45%,transparent_70%)] blur-3xl pointer-events-none" />

            {/* Framed brand image — clickable to strainscanpro.com */}
            <a
              href="https://strainscanpro.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="strainscan-brand-link"
              aria-label="Visit StrainScanPro.com"
              className="relative block rounded-[24px] border border-[rgba(160,107,255,0.55)] bg-[#050208] p-2 shadow-[0_20px_80px_rgba(74,222,128,0.22)] overflow-hidden group cursor-pointer transition-transform duration-500 hover:scale-[1.01]"
            >
              {/* Corner brackets */}
              <span className="absolute top-3 left-3 h-5 w-5 border-t-2 border-l-2 border-[#a06bff] rounded-tl-md z-10 pointer-events-none" />
              <span className="absolute top-3 right-3 h-5 w-5 border-t-2 border-r-2 border-[#a06bff] rounded-tr-md z-10 pointer-events-none" />
              <span className="absolute bottom-3 left-3 h-5 w-5 border-b-2 border-l-2 border-[#a06bff] rounded-bl-md z-10 pointer-events-none" />
              <span className="absolute bottom-3 right-3 h-5 w-5 border-b-2 border-r-2 border-[#a06bff] rounded-br-md z-10 pointer-events-none" />

              {/* Top HUD pill */}
              <div className="absolute top-5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-1 rounded-full bg-[rgba(11,6,18,0.72)] border border-[rgba(74,222,128,0.55)] backdrop-blur">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#4ade80] shadow-[0_0_10px_rgba(74,222,128,0.85)] animate-pulse" />
                <span className="font-mono-tech text-[10px] tracking-[0.24em] text-[#86efac]">SYSTEM ONLINE</span>
              </div>

              <img
                src="/images/strainscan-brand.webp"
                alt="StrainScan Pro — powered by Medical Care Technologies Inc."
                loading="lazy"
                className="relative w-full h-auto object-contain block rounded-[18px] transition-opacity duration-300 group-hover:opacity-95"
              />
            </a>

            {/* Device caption */}
            <div className="mt-4 flex items-center justify-between text-[11px] font-mono-tech tracking-[0.22em] text-[#a991d6] gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#a06bff] animate-pulse" />
                iPAD AIR · ENTERPRISE DEPLOYMENT
              </span>
              <span className="text-[#5a3a93]">/</span>
              <span>ONE PLATFORM · ANY VERTICAL</span>
            </div>
          </div>
        </div>

        {/* RIGHT: TAM verticals + narrative */}
        <div className="lg:col-span-6">
          <div className="font-mono-tech text-[10px] text-[#a06bff] tracking-[0.18em]">
            MULTI-VERTICAL OPPORTUNITY
          </div>
          <h3 className="font-display text-2xl lg:text-3xl font-bold text-white mt-2 leading-tight">
            One platform. <span className="text-gradient-violet">Multi-trillion-dollar reach.</span>
          </h3>
          <p className="mt-3 text-[#b9add0] text-[15px] leading-relaxed">
            The same vision engine that identifies strains and grades cannabis flower
            adapts to any product that needs to be recognized, inspected, or tracked
            at scale — unifying quality control across every step of the supply chain.
          </p>

          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {verticals.map((v, i) => (
              <div
                key={v.k}
                data-testid={`strainscan-vertical-${i + 1}`}
                className="glass-panel p-4 relative"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="font-display text-white text-[15px] lg:text-[16px]">
                    {v.k}
                  </div>
                  <div className="font-mono-tech text-[11px] tracking-[0.12em] text-[#d6b8ff] shrink-0">
                    {v.tam}
                  </div>
                </div>
                <div className="mt-1.5 text-[12.5px] text-[#b9add0] leading-relaxed">
                  {v.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-12 flex justify-center">
        <a
          href="https://www.strainscanpro.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="strainscan-cta"
          className="btn-primary group"
        >
          Visit StrainScanPro.com
          <ArrowUpRight
            size={18}
            className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </a>
      </div>

      <p className="mt-6 text-center font-mono-tech text-[11px] tracking-[0.24em] text-[#a991d6]">
        ENTERPRISE-LEVEL · PROPRIETARY SOFTWARE · (OTC TICKER: MDCE)
      </p>
    </div>
  );
}

function SnapshotShowcase() {
  const slides = [
    {
      src: "/images/sr-1-newlook.webp",
      label: "NEW LOOK",
      title: "AI-Powered Recipe App",
      caption: "Now making more than just food.",
    },
    {
      src: "/images/sr-2-fridge.webp",
      label: "FRIDGE",
      title: "Open Your Fridge",
      caption: "Snap what you have — see meal ideas.",
    },
    {
      src: "/images/sr-2-screenshot.webp",
      label: "SCREENSHOT",
      title: "Screenshot → Recipe",
      caption: "Turn any food pic into a full recipe.",
    },
    {
      src: "/images/sr-3-groceries.webp",
      label: "GROCERIES",
      title: "Snap Your Groceries",
      caption: "Pantry photo → personalized meal ideas.",
    },
    {
      src: "/images/sr-4-inspires.webp",
      label: "INSPIRE",
      title: "Endless Inspiration",
      caption: "Any recipe. Any time. Endless possibilities.",
    },
  ];

  return (
    <section id="snapshot-recipes" className="relative py-20 lg:py-28" data-testid="snapshot-showcase">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <div className="relative text-center max-w-3xl mx-auto mb-8 lg:mb-10">
        <div className="section-num">08 / SNAPSHOT RECIPES</div>
        <p className="mt-2 text-[#a06bff] font-mono-tech text-[10.5px] tracking-[0.24em]">FLAGSHIP MOBILE APP · LIVE</p>
        <h2 className="font-display text-3xl lg:text-5xl font-bold mt-3 leading-[1.05]">
          <span className="text-white">Snapshot Recipes — </span>
          <span className="text-gradient-violet">Launched & Newly Upgraded</span>
        </h2>
        <p className="mt-4 text-[#a991d6] font-mono-tech text-[11px] tracking-[0.24em]">
          AI-POWERED RECIPE APP · NOW MAKING MORE THAN JUST FOOD
        </p>
      </div>

      <MelanomaCarousel
        slides={slides}
        variant="card"
        testid="snapshot-carousel"
        ariaLabel="Snapshot Recipes promotional screenshots"
      />

      <p className="mt-10 text-center font-mono-tech text-[11px] tracking-[0.24em] text-[#a991d6]">
        AVAILABLE NOW · APP STORE · GOOGLE PLAY
      </p>
      </div>
    </section>
  );
}

function Subsidiaries() {
  const subs = [
    {
      name: "Infinite Auctions LLC",
      url: "https://www.infiniteauctions.com",
      img: IMG.infinite,
      icon: Award,
      body: "Infinite Auctions is a premier auction house specializing in investment-grade sports, entertainment, and pop culture memorabilia, known for its elite offerings and exclusive access to some of the most iconic game-worn and signed artifacts in the industry. With a commitment to authenticity, innovation, and collector confidence, Infinite Auctions combines high-impact marketing with cutting-edge technologies — including photo-match verification and AI-enhanced provenance tools — to deliver a world-class auction experience.",
    },
    {
      name: "Real Game Used LLC",
      url: "https://www.realgameused.com",
      img: IMG.rgu,
      icon: Microscope,
      body: "Real Game Used (RGU) is a disruptive authentication technology company redefining how memorabilia is verified through advanced AI and image analysis. RGU leverages proprietary machine learning algorithms and computer vision systems to deliver forensic-level photo matching and authenticity verification for game-used and entertainment memorabilia. With multiple patent filings underway and high-profile partnerships in the auction and collectibles space, RGU is positioned as a category leader at the intersection of technology, trust, and cultural preservation.",
    },
  ];
  return (
    <section id="subsidiaries" className="relative py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="section-num">10 / SUBSIDIARIES</div>
          <h2 className="font-display text-4xl lg:text-6xl font-bold mt-3 leading-[1.05]">
            <span className="text-white">Revenue-generating </span>
            <span className="text-gradient-violet">subsidiaries.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {subs.map((s, i) => (
            <div
              key={s.name}
              data-testid={`subsidiary-card-${i}`}
              className="glass-panel p-6 lg:p-8 flex flex-col"
            >
              <div className="rounded-2xl overflow-hidden border border-[rgba(160,107,255,0.25)] bg-[#0b0612] mb-6 p-3">
                <img src={s.img} alt={s.name} className="w-full h-auto object-contain block rounded-xl" loading="lazy" />
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 grid place-items-center rounded-lg bg-[rgba(160,107,255,0.12)] border border-[rgba(160,107,255,0.35)]">
                  <s.icon size={16} className="text-[#d6b8ff]" />
                </div>
                <h3 className="font-display text-2xl text-white">{s.name}</h3>
              </div>
              <p className="text-[#cfc4e6] text-[15px] leading-relaxed">{s.body}</p>
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`subsidiary-link-${i}`}
                className="mt-6 inline-flex items-center gap-2 text-[#d6b8ff] hover:text-white transition self-start"
              >
                {s.url.replace(/https?:\/\/(www\.)?/, "")} <ArrowUpRight size={16} />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" className="relative py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="glass-panel p-8 lg:p-14 relative overflow-hidden">
          <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] bg-[radial-gradient(circle,rgba(138,61,255,0.4),transparent_70%)] blur-3xl" />
          <div className="grid lg:grid-cols-12 gap-10 relative">
            <div className="lg:col-span-7">
              <div className="section-num">11 / CONTACT</div>
              <h2 className="font-display text-4xl lg:text-6xl font-bold text-white mt-3 leading-[1.05]">
                Working hard today <br />
                <span className="text-gradient-violet">for a better tomorrow.</span>
              </h2>
              <p className="mt-5 text-[#cfc4e6] text-lg max-w-xl">
                Turning your smart device into a personal AI tool for health & wellness.
              </p>

              <div className="mt-10 space-y-5">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-[rgba(160,107,255,0.12)] border border-[rgba(160,107,255,0.35)] shrink-0">
                    <MapPin size={18} className="text-[#d6b8ff]" />
                  </div>
                  <div>
                    <div className="font-mono-tech text-[10px] text-[#a991d6]">HEADQUARTERS</div>
                    <div className="text-white text-[15px] mt-1">
                      1910 South Stapley Drive, Suite 221<br />
                      Mesa, AZ 85204, USA
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-[rgba(160,107,255,0.12)] border border-[rgba(160,107,255,0.35)] shrink-0">
                    <Globe size={18} className="text-[#d6b8ff]" />
                  </div>
                  <div>
                    <div className="font-mono-tech text-[10px] text-[#a991d6]">TICKER</div>
                    <div className="text-white text-[15px] mt-1">OTC PINK · MDCE</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-[rgba(160,107,255,0.12)] border border-[rgba(160,107,255,0.35)] shrink-0">
                    <Mail size={18} className="text-[#d6b8ff]" />
                  </div>
                  <div>
                    <div className="font-mono-tech text-[10px] text-[#a991d6]">EMAIL</div>
                    <a
                      href="mailto:marshall@medicalcaretechnologies.com"
                      data-testid="contact-email-link"
                      className="text-white text-[15px] mt-1 inline-flex items-center gap-2 hover:text-[#d6b8ff] break-all"
                    >
                      marshall@medicalcaretechnologies.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-[rgba(160,107,255,0.12)] border border-[rgba(160,107,255,0.35)] shrink-0">
                    <XIcon size={18} className="text-[#d6b8ff]" />
                  </div>
                  <div>
                    <div className="font-mono-tech text-[10px] text-[#a991d6]">CONNECT</div>
                    <a
                      href="https://www.x.com/medicalcaretech"
                      target="_blank"
                      rel="noreferrer noopener"
                      data-testid="contact-x-link"
                      className="text-white text-[15px] mt-1 inline-flex items-center gap-2 hover:text-[#d6b8ff]"
                    >
                      @medicalcaretech <ArrowUpRight size={14} />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="glass-panel p-6 lg:p-7 !rounded-2xl">
                <div className="font-display text-xl text-white">Get in touch</div>
                <p className="text-[#a991d6] text-sm mt-1">
                  Investor & partnership inquiries.
                </p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const data = new FormData(e.currentTarget);
                    const subject = encodeURIComponent("MDCE Inquiry");
                    const body = encodeURIComponent(
                      `Name: ${data.get("name")}\nEmail: ${data.get("email")}\nPhone: ${data.get("phone")}\n\n${data.get("message")}`
                    );
                    window.location.href = `mailto:marshall@medicalcaretechnologies.com?subject=${subject}&body=${body}`;
                  }}
                  className="mt-5 space-y-3"
                >
                  <input
                    name="name"
                    required
                    placeholder="Name"
                    data-testid="contact-input-name"
                    className="w-full px-4 py-3 rounded-xl bg-[#0b0612] border border-[rgba(160,107,255,0.25)] focus:border-[rgba(160,107,255,0.7)] outline-none text-white placeholder-[#7a6d96] text-sm transition"
                  />
                  <input
                    name="email"
                    required
                    type="email"
                    placeholder="Email *"
                    data-testid="contact-input-email"
                    className="w-full px-4 py-3 rounded-xl bg-[#0b0612] border border-[rgba(160,107,255,0.25)] focus:border-[rgba(160,107,255,0.7)] outline-none text-white placeholder-[#7a6d96] text-sm transition"
                  />
                  <input
                    name="phone"
                    placeholder="Phone"
                    data-testid="contact-input-phone"
                    className="w-full px-4 py-3 rounded-xl bg-[#0b0612] border border-[rgba(160,107,255,0.25)] focus:border-[rgba(160,107,255,0.7)] outline-none text-white placeholder-[#7a6d96] text-sm transition"
                  />
                  <textarea
                    name="message"
                    rows={4}
                    placeholder="Tell us about your inquiry…"
                    data-testid="contact-input-message"
                    className="w-full px-4 py-3 rounded-xl bg-[#0b0612] border border-[rgba(160,107,255,0.25)] focus:border-[rgba(160,107,255,0.7)] outline-none text-white placeholder-[#7a6d96] text-sm transition resize-none"
                  />
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    className="btn-primary w-full justify-center"
                  >
                    Send Message <ArrowUpRight size={16} />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="relative pt-12 pb-10 border-t border-[rgba(160,107,255,0.18)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="MDCE" loading="lazy" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-display text-white font-semibold">Medical Care Technologies Inc.</div>
              <div className="text-[#8d83a6] text-xs mt-0.5">
                1910 S Stapley Drive, Suite 221, Mesa, AZ 85204
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://www.x.com/medicalcaretech"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="footer-x-link"
              className="h-10 w-10 grid place-items-center rounded-full border border-[rgba(160,107,255,0.3)] hover:border-[rgba(160,107,255,0.7)] hover:bg-[rgba(160,107,255,0.08)] transition text-white"
              aria-label="X / Twitter"
            >
              <XIcon size={16} />
            </a>
            <a
              href="https://www.youtube.com/channel/UCtXjH8i6vr5bU4dvYuGt1hg"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="footer-youtube-link"
              className="h-10 px-4 grid place-items-center rounded-full border border-[rgba(160,107,255,0.3)] hover:border-[rgba(160,107,255,0.7)] hover:bg-[rgba(160,107,255,0.08)] transition text-white text-sm"
            >
              YouTube Channel
            </a>
          </div>
        </div>
        <div className="mt-8 divider-glow" />
        <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-[#7a6d96]">
          <div>© {new Date().getFullYear()} Medical Care Technologies Inc. — All Rights Reserved.</div>
          <div className="font-mono-tech tracking-[0.2em]">
            OTC PINK · MDCE · WORKING HARD TODAY FOR A BETTER TOMORROW
          </div>
        </div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div className="App relative" data-testid="landing-root">
      <NeuralBackground />
      <div className="relative z-10 subtle-grid">
        <Nav />
        <main>
          <Hero />
          <Applications />
          <TestingResults />
          <EnterpriseVision />
          <About />
          <StockChart />
          <Roadmap />
          <SnapshotShowcase />
          <Leadership />
          <Subsidiaries />
          <Contact />
        </main>
        <Footer />
      </div>
      <MusicToggle />
      <TouchSplash />
    </div>
  );
}

export default App;
